"use client";

import {
  datadogRum,
  type RumInitConfiguration,
  type Site,
  type MatchOption,
  type User,
} from "@datadog/browser-rum";
import { getRuntimeEnv } from "@utils/extractEnvFromScript";

type RuntimeEnv = ReturnType<typeof getRuntimeEnv>;

let isRumInitialized = false;

const parseSampleRate = (value: string | undefined, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getDatadogSite = (site: string | undefined): Site => {
  return (site || "datadoghq.com") as Site;
};

const getDatadogService = (env: RuntimeEnv) => {
  return env?.NEXT_PUBLIC_DATADOG_SERVICE || "ackodrive-next15-web";
};

const buildAllowedTracingUrls = (): MatchOption[] => {
  if (typeof window === "undefined") {
    return [];
  }

  return [
    (url: string) => {
      try {
        const requestUrl = new URL(url, window.location.origin);

        // Only attach Datadog tracing headers to same-origin Next API routes.
        // Cross-origin catalog/workspace/BFF requests require explicit CORS
        // support for Datadog trace headers and can fail preflight otherwise.
        return (
          requestUrl.origin === window.location.origin &&
          requestUrl.pathname.startsWith("/api/")
        );
      } catch {
        return false;
      }
    },
  ];
};

const getRumConfig = (env: RuntimeEnv): RumInitConfiguration | null => {
  const applicationId = env?.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = env?.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    return null;
  }

  return {
    applicationId,
    clientToken,
    site: getDatadogSite(env?.NEXT_PUBLIC_DATADOG_SITE),
    service: getDatadogService(env),
    env: env?.ACKO_ENV || "unknown",
    version: env?.APP_VERSION || undefined,
    sessionSampleRate: parseSampleRate(
      env?.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
      100
    ),
    sessionReplaySampleRate: parseSampleRate(
      env?.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
      0
    ),
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    allowedTracingUrls: buildAllowedTracingUrls(),
    defaultPrivacyLevel: "mask-user-input",
  };
};

export const initDatadogRum = () => {
  if (isRumInitialized) {
    return datadogRum;
  }

  const config = getRumConfig(getRuntimeEnv());
  if (!config) {
    return null;
  }

  datadogRum.init(config);
  isRumInitialized = true;

  return datadogRum;
};

export const addDatadogAction = (
  name: string,
  context?: Record<string, unknown>
) => {
  const rum = initDatadogRum();
  if (!rum) {
    return;
  }

  rum.addAction(name, context);
};

export const addDatadogError = (
  error: unknown,
  context?: Record<string, unknown>
) => {
  const rum = initDatadogRum();
  if (!rum) {
    return;
  }

  const normalizedError =
    error instanceof Error ? error : new Error(typeof error === "string" ? error : "Unknown error");

  rum.addError(normalizedError, context);
};

export const setDatadogRumUser = (
  user: User & Record<string, unknown>
) => {
  const rum = initDatadogRum();
  if (!rum) {
    return;
  }

  rum.setUser(user);
};

export const clearDatadogRumUser = () => {
  const rum = initDatadogRum();
  if (!rum) {
    return;
  }

  rum.clearUser();
};
